import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Users, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Building,
  DollarSign,
  Calendar,
  RefreshCw,
  Settings,
  Filter,
  Search,
  Award,
  Target,
  BarChart3,
  PieChart,
  Activity,
  Globe,
  FileText,
  Download,
  Eye,
  Star,
  Zap,
  Crown,
  Flag,
  XCircle,
  MapPin,
  Info,
  User,
  Send
} from 'lucide-react';
import { 
  Card, 
  Table, 
  Tag, 
  Progress, 
  Button, 
  Input, 
  Select, 
  Modal, 
  Form, 
  InputNumber,
  message,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Tooltip,
  Badge,
  Avatar,
  Divider,
  Empty,
  Spin,
  Alert,
  Tabs,
  Timeline,
  List,
  Descriptions,
  Checkbox
} from 'antd';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { collection, query, orderBy, doc, updateDoc, getDocs, getDoc, setDoc, serverTimestamp, where, onSnapshot, getDocFromCache, getDocFromServer, limit, startAfter } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig.jsx';
import dayjs from 'dayjs';
import messageService from '../services/messageService';

const { Title, Text } = Typography;
const { Option } = Select;

// 고급 CSS 스타일
const advancedStyles = `
  .modern-pagination .ant-pagination-item {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    transition: all 0.3s ease;
    min-width: 28px;
    height: 28px;
    line-height: 28px;
    font-size: 12px;
  }
  
  .modern-pagination .ant-pagination-item:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    transform: translateY(-1px);
  }
  
  .modern-pagination .ant-pagination-item-active {
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    border-color: #3b82f6;
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  .modern-pagination .ant-pagination-prev,
  .modern-pagination .ant-pagination-next {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    transition: all 0.3s ease;
    min-width: 28px;
    height: 28px;
    line-height: 28px;
  }
  
  .modern-pagination .ant-pagination-prev:hover,
  .modern-pagination .ant-pagination-next:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    transform: translateY(-1px);
  }
  
  .modern-table .ant-table-thead > tr > th {
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: #374151;
    padding: 10px 12px;
    font-size: 12px;
  }
  
  .modern-table .ant-table-tbody > tr > td {
    padding: 10px 12px;
  }

  .modern-table .ant-table-tbody > tr:hover > td {
    background: linear-gradient(135deg, #eff6ff, #dbeafe) !important;
  }
  
  .modern-modal .ant-modal-header {
    border-bottom: 1px solid #f3f4f6;
    padding: 24px 24px 16px;
    background: linear-gradient(135deg, #f8fafc, #ffffff);
  }
  
  .modern-modal .ant-modal-body {
    padding: 20px;
    background: #ffffff;
  }
  
  .modern-modal .ant-modal-content {
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
  
  .gradient-text {
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .glass-effect {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }
  
  .floating-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .floating-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
`;

// 스타일 주입
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = advancedStyles;
  document.head.appendChild(styleSheet);
}

// 통합된 Policy Compliance 계산 함수 import
import { calculatePolicyCompliance } from '../../../shared/utils/policyComplianceCalculator';
import { calculateUsersWithCompliance, calculatePolicyComplianceStats } from '../../../shared/utils/policyComplianceStatsCalculator';

const ENCOURAGE_TEMPLATES = [
  {
    key: 'my_personal',
    label: '미얀마어 · 개인 맞춤',
    language: 'myanmar',
    build: ({ compliance, required }) => ({
      title: '💝 မိသားစုထံ ငွေလွှဲရန် အချိန်ရောက်ပါပြီ',
      body: `သင့်လက်ရှိ မူဝါဒလိုက်နာမှုနှုန်းမှာ ${compliance}% ဖြစ်ပါသည်။ လစဉ် ${required} USD ငွေလွှဲပါက မူဝါဒစံနှုန်းနှင့် ကိုက်ညီပါမည်။ Kairos အက်ပ်တွင် ယခုပဲ ငွေလွှဲလိုက်ပါ။`,
    }),
  },
  {
    key: 'my_gentle',
    label: '미얀마어 · 가족 송금 권유',
    language: 'myanmar',
    build: () => ({
      title: '💝 မိသားစုများထံ ငွေလွှဲပြောင်းပါ',
      body: 'သင့်မိသားစုများကို စိတ်အေးချမ်းမှု ပေးနိုင်ရန် ငွေလွှဲပြောင်းမှု ပြုလုပ်ပါ။ မူဝါဒ လိုက်နာမှုကိုလည်း ထိန်းသိမ်းနိုင်ပါသည်။',
    }),
  },
  {
    key: 'ko',
    label: '한국어',
    language: 'korean',
    build: ({ name, compliance, required }) => ({
      title: '💝 가족에게 송금할 시간이에요',
      body: `${name ? `${name}님, ` : ''}현재 정책 준수율은 ${compliance}%입니다. 매월 ${required} USD 이상 송금하시면 정책 기준을 충족할 수 있어요. Kairos 앱에서 지금 바로 송금해 보세요.`,
    }),
  },
  {
    key: 'en',
    label: 'English',
    language: 'english',
    build: ({ name, compliance, required }) => ({
      title: '💝 Time to send money home',
      body: `${name ? `Hi ${name}, ` : ''}your current policy compliance is ${compliance}%. Sending at least ${required} USD each month keeps you compliant. Send now in the Kairos app.`,
    }),
  },
];

const PolicyCompliancePage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingCache, setUpdatingCache] = useState(false); // 캐시 업데이트 중 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployment, setFilterEmployment] = useState('all');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState({ requiredMonthlyAmount: 300 });
  const [form] = Form.useForm();
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [sendingWarning, setSendingWarning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [allUsersCache, setAllUsersCache] = useState([]); // 전체 사용자 데이터 캐시 (메모리)
  
  // Non-Compliant 사용자 최적화 상태
  const [nonCompliantLoading, setNonCompliantLoading] = useState(false);
  const [realTimeMonitoring, setRealTimeMonitoring] = useState(false);
  const [performanceStats, setPerformanceStats] = useState({
    queryTime: 0,
    cacheHit: false,
    lastUpdate: null
  });
  
  // Non-Compliant 사용자 선택 및 알림 상태
  const [selectedNonCompliantUsers, setSelectedNonCompliantUsers] = useState([]);
  const [sendingRemittanceNotification, setSendingRemittanceNotification] = useState(false);

  // 상세 모달 — 송금 독려 메시지
  const [encourageTemplate, setEncourageTemplate] = useState('my_personal');
  const [encourageTitle, setEncourageTitle] = useState('');
  const [encourageBody, setEncourageBody] = useState('');
  const [encourageAlsoInbox, setEncourageAlsoInbox] = useState(false);
  const [sendingEncourage, setSendingEncourage] = useState(false);
  const [encourageSentAt, setEncourageSentAt] = useState(null);
  
  // Non-Compliant 사용자 상태 (직접 관리)
  const [nonCompliantUsers, setNonCompliantUsers] = useState([]);
  
  // 통계 데이터 상태
  const [statsData, setStatsData] = useState({
    totalUsers: 0,
    nonCompliantUsers: 0,
    averageComplianceRate: 0,
    complianceAchieved: 0,
    severityStats: {
      critical: 0,
      warning: 0,
      moderate: 0
    }
  });
  
  // Cloud Functions 호출
  const getNonCompliantUsers = httpsCallable(functions, 'getNonCompliantUsers');
  const getNonCompliantStats = httpsCallable(functions, 'getNonCompliantStats');

  // Non-Compliant 사용자 조회 (최적화된 버전)
  const loadNonCompliantUsers = async (page = 1, pageSize = 20, severity = 'all') => {
    setNonCompliantLoading(true);
    const startTime = performance.now();
    
    try {
      console.log('🚀 Non-Compliant 사용자 조회 시작:', { page, pageSize, severity });
      
      // 심각도별 필터 설정
      let minCompliance = 0, maxCompliance = 99;
      switch (severity) {
        case 'critical': minCompliance = 0; maxCompliance = 24; break;
        case 'warning': minCompliance = 25; maxCompliance = 49; break;
        case 'moderate': minCompliance = 50; maxCompliance = 99; break;
        default: minCompliance = 0; maxCompliance = 99; break;
      }
      
      const result = await getNonCompliantUsers({
        page,
        pageSize,
        minCompliance,
        maxCompliance,
        employmentStatus: 'employed',
        sortBy: 'compliance',
        sortOrder: 'asc'
      });
      
      if (result.data.success) {
        const { users: userData, pagination, performance } = result.data.data;
        
        setNonCompliantUsers(userData);
        const executionTime = performance.now() - startTime;
        
        setPerformanceStats({
          queryTime: executionTime,
          cacheHit: performance.cacheHit,
          lastUpdate: new Date()
        });
        
        console.log('✅ Non-Compliant 사용자 조회 완료:', {
          usersCount: userData.length,
          totalCount: pagination.totalCount,
          executionTime: `${executionTime.toFixed(2)}ms`,
          cacheHit: performance.cacheHit
        });
      } else {
        throw new Error('Failed to fetch non-compliant users');
      }
      
    } catch (error) {
      console.error('❌ Non-Compliant 사용자 조회 오류:', error);
      message.error('Failed to load non-compliant users');
    } finally {
      setNonCompliantLoading(false);
    }
  };

  // 실시간 모니터링 시작/중지
  const toggleRealTimeMonitoring = () => {
    setRealTimeMonitoring(!realTimeMonitoring);
  };

  // 실시간 모니터링 효과
  useEffect(() => {
    if (realTimeMonitoring) {
      const interval = setInterval(() => {
        loadUsersWithPagination(currentPage, pageSize); // 페이지네이션 함수 사용
      }, 30000); // 30초마다 업데이트
      
      return () => clearInterval(interval);
    }
  }, [realTimeMonitoring]);

  // Non-Compliant 사용자 빠른 조회 (캐시 우선 방식)
  const loadNonCompliantUsersOptimized = async (severity = 'all') => {
    setNonCompliantLoading(true);
    const startTime = performance.now();
    
    try {
      console.log('🚀 Non-Compliant 사용자 최적화 조회 시작:', { severity });
      
      // 심각도별 필터 설정
      let minCompliance = 0, maxCompliance = 99;
      switch (severity) {
        case 'critical': minCompliance = 0; maxCompliance = 24; break;
        case 'warning': minCompliance = 25; maxCompliance = 49; break;
        case 'moderate': minCompliance = 50; maxCompliance = 99; break;
        default: minCompliance = 0; maxCompliance = 99; break;
      }
      
      // 1. 캐시에서 먼저 로드 시도 (배치로 생성된 캐시 컬렉션에서)
      try {
        const artifactsDocRef = doc(db, `artifacts/${appId}`);
        const adminCollectionRef = collection(artifactsDocRef, 'admin');
        const statsDocRef = doc(adminCollectionRef, 'policyComplianceStats');
        
        // 서버에서 최신 데이터 가져오기
        let statsSnapshot;
        try {
          statsSnapshot = await getDocFromServer(statsDocRef);
        } catch (serverError) {
          console.warn('⚠️ [Non-Compliant] 서버에서 통계 로드 실패, 일반 getDoc 사용:', serverError.message);
          statsSnapshot = await getDoc(statsDocRef);
        }
        
        if (statsSnapshot.exists()) {
          // 캐시 컬렉션에서 모든 사용자 데이터 가져오기 (서버에서 최신 데이터)
          const cacheCollectionRef = collection(statsDocRef, 'policyComplianceCache');
          let cacheSnapshot;
          try {
            cacheSnapshot = await getDocsFromServer(cacheCollectionRef);
          } catch (serverError) {
            console.warn('⚠️ 서버에서 캐시 로드 실패, 일반 getDocs 사용:', serverError.message);
            cacheSnapshot = await getDocs(cacheCollectionRef);
          }
          
          // Non-Compliant 사용자 필터링 (compliance < 100, employed 상태, 심각도별 필터)
          const nonCompliantUsersData = cacheSnapshot.docs
            .map(doc => {
              const data = doc.data();
              return {
                id: data.userId,
                userId: data.userId,
                name: data.name,
                email: data.email,
                companyName: data.companyName,
                employmentStatus: data.employmentStatus === 'employed' ? 'Employed' : 
                                data.employmentStatus === 'unemployed' ? 'Unemployed' : 
                                data.employmentStatus === 'no_employment_data' ? 'No Employment Data' : 'Unknown',
                status: data.employmentStatus,
                compliance: data.compliance,
                monthlyAverage: data.monthlyAverage,
                totalRemittance: data.totalRemittance,
                monthsEmployed: data.monthsEmployed,
                contractStartDate: data.contractStartDate,
                contractEndDate: data.contractEndDate,
                requiredMonthlyAmount: data.requiredMonthlyAmount,
                lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : data.lastUpdated,
                lastCalculated: data.lastCalculated?.toDate ? data.lastCalculated.toDate() : data.lastCalculated
              };
            })
            .filter(user => {
              // employed 상태이고 compliance < 100인 사용자만
              if (user.status !== 'employed' || user.compliance >= 100) {
                return false;
              }
              // 심각도별 필터 적용
              return user.compliance >= minCompliance && user.compliance <= maxCompliance;
            })
            .sort((a, b) => {
              // compliance 낮은 순으로 정렬 (가장 심각한 사용자 우선)
              if (a.compliance !== b.compliance) {
                return a.compliance - b.compliance;
              }
              // 동일한 compliance면 이름순
              return (a.name || a.email || '').localeCompare(b.name || b.email || '');
            });
          
          setNonCompliantUsers(nonCompliantUsersData);
          
          const executionTime = performance.now() - startTime;
          
          setPerformanceStats({
            queryTime: executionTime,
            cacheHit: true,
            lastUpdate: new Date()
          });
          
          console.log(`✅ [Non-Compliant] 캐시에서 로드 완료: ${nonCompliantUsersData.length}명 (${executionTime.toFixed(2)}ms)`);
          console.log('📍 데이터 출처: 배치 작업으로 생성된 캐시 컬렉션 (policyComplianceCache)');
          
          return; // 캐시에서 성공적으로 로드했으면 종료
        } else {
          console.log('⚠️ [Non-Compliant] 캐시 통계 데이터 없음, Cloud Function으로 폴백');
        }
      } catch (cacheError) {
        console.warn('⚠️ [Non-Compliant] 캐시 로드 실패, Cloud Function으로 폴백:', cacheError.message);
      }
      
      // 2. 캐시가 없거나 실패한 경우 Cloud Function 사용
      console.log('🔄 [Non-Compliant] Cloud Functions에서 데이터 로드 시작...');
      const result = await getNonCompliantUsers({
        page: 1,
        pageSize: 1000, // 모든 Non-Compliant 사용자 조회
        minCompliance,
        maxCompliance,
        employmentStatus: 'employed',
        sortBy: 'compliance',
        sortOrder: 'asc'
      });
      
      if (result.data.success) {
        const { users: userData, totalCount, queryTime, cacheHit } = result.data.data;
        
        // Non-Compliant 사용자 데이터를 직접 설정
        setNonCompliantUsers(userData);
        
        const executionTime = performance.now() - startTime;
        
        setPerformanceStats({
          queryTime: executionTime,
          cacheHit: cacheHit || false,
          lastUpdate: new Date()
        });
        
        console.log('✅ [Non-Compliant] Cloud Function에서 로드 완료:', {
          usersCount: userData.length,
          executionTime: `${executionTime.toFixed(2)}ms`,
          cacheHit: cacheHit
        });
        console.log('📍 데이터 출처: Cloud Function (getNonCompliantUsers) - 캐시 없음');
      } else {
        throw new Error('Failed to fetch non-compliant users');
      }
      
    } catch (error) {
      console.error('❌ Non-Compliant 사용자 최적화 조회 오류:', error);
      message.error('Failed to load non-compliant users');
    } finally {
      setNonCompliantLoading(false);
    }
  };

  // Non-Compliant 사용자 선택 핸들러
  const handleSelectNonCompliantUser = (userId, checked) => {
    if (checked) {
      setSelectedNonCompliantUsers(prev => [...prev, userId]);
    } else {
      setSelectedNonCompliantUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // 모든 Non-Compliant 사용자 선택/해제
  const handleSelectAllNonCompliantUsers = () => {
    if (selectedNonCompliantUsers.length === nonCompliantUsers.length) {
      setSelectedNonCompliantUsers([]);
    } else {
      setSelectedNonCompliantUsers(nonCompliantUsers.map(user => user.id));
    }
  };

  // 송금 권고 푸시알림 전송
  const handleSendRemittanceNotification = async () => {
    if (selectedNonCompliantUsers.length === 0) {
      message.warning('Please select users to send notification');
      return;
    }

    setSendingRemittanceNotification(true);
    
    try {
      console.log('🚀 송금 권고 푸시알림 전송 시작:', {
        selectedUsers: selectedNonCompliantUsers.length,
        totalUsers: nonCompliantUsers.length
      });

      // 선택된 사용자들의 정보 가져오기
      const selectedUsers = nonCompliantUsers.filter(user => 
        selectedNonCompliantUsers.includes(user.id)
      );

      // 송금 권고 메시지 구성
      const notificationData = {
        userIds: selectedNonCompliantUsers,
        title: '송금 권고 알림',
        message: `정책 준수를 위해 월 평균 송금액을 달성해주세요. 현재 준수율이 낮아 개선이 필요합니다.`,
        type: 'remittance_reminder',
        language: 'korean',
        sender: 'admin'
      };

      // 백엔드 API 호출
      const response = await fetch('/api/notification/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notificationData),
        mode: 'cors', // 🔥 CORS 모드 명시
        credentials: 'include' // 🔥 인증 정보 포함
      });

      const result = await response.json();

      if (result.success) {
        message.success(`송금 권고 알림을 ${selectedNonCompliantUsers.length}명에게 전송했습니다 (성공: ${result.successCount || selectedNonCompliantUsers.length}명)`);
        setSelectedNonCompliantUsers([]);
        
        console.log('✅ 송금 권고 푸시알림 전송 완료:', {
          successCount: result.successCount,
          failureCount: result.failureCount
        });
      } else {
        throw new Error(result.error || 'Failed to send notification');
      }

    } catch (error) {
      console.error('❌ 송금 권고 푸시알림 전송 오류:', error);
      message.error('Failed to send remittance notification');
    } finally {
      setSendingRemittanceNotification(false);
    }
  };

  // 페이지네이션된 사용자 데이터 로드 (캐시 우선 방식)
  const loadUsersWithPagination = async (page = 1, size = 15) => {
    setLoading(true);
    try {
      console.log(`📋 Starting Policy Compliance data load (Page ${page}, Size ${size})...`);
      
      // 1. Try to load from cache first
      let usersData = [];
      let statsFromCache = null;
      
      try {
        console.log('💾 캐시 데이터 로드 시도...');
        
        // 타임아웃 설정 (10초)
        const cacheLoadPromise = (async () => {
          try {
            // Load stats from cache (서버에서 최신 데이터 가져오기)
            const artifactsDocRef = doc(db, `artifacts/${appId}`);
            const adminCollectionRef = collection(artifactsDocRef, 'admin');
            const statsDocRef = doc(adminCollectionRef, 'policyComplianceStats');
            let statsSnapshot;
            try {
              statsSnapshot = await getDocFromServer(statsDocRef);
            } catch (serverError) {
              console.warn('⚠️ 서버에서 통계 로드 실패, 일반 getDoc 사용:', serverError.message);
              statsSnapshot = await getDoc(statsDocRef);
            }
            
            if (statsSnapshot.exists()) {
              statsFromCache = statsSnapshot.data();
              const cacheAge = statsFromCache.lastUpdated 
                ? new Date() - statsFromCache.lastUpdated.toDate()
                : Infinity;
              const cacheAgeHours = cacheAge / (1000 * 60 * 60);
              
              console.log(`📊 캐시 통계 발견 (${cacheAgeHours.toFixed(1)}시간 전 업데이트)`);
              
              // 전체 사용자 수 설정
              setTotalUsersCount(statsFromCache.totalUsers || 0);
              
              // 캐시 컬렉션에서 모든 데이터 가져오기 (배치 컬렉션에서)
              // 메모리에 저장하여 페이지 변경 시 빠르게 처리
              const cacheCollectionRef = collection(statsDocRef, 'policyComplianceCache');
              let cacheSnapshot;
              try {
                // 모든 캐시 데이터를 한 번에 가져오기 (배치 작업으로 생성된 컬렉션)
                cacheSnapshot = await getDocsFromServer(cacheCollectionRef);
              } catch (serverError) {
                console.warn('⚠️ 서버에서 캐시 로드 실패, 일반 getDocs 사용:', serverError.message);
                cacheSnapshot = await getDocs(cacheCollectionRef);
              }
              
              usersData = cacheSnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: data.userId,
                  userId: data.userId,
                  name: data.name,
                  email: data.email,
                  companyName: data.companyName,
                  employmentStatus: data.employmentStatus === 'employed' ? 'Employed' : 
                                  data.employmentStatus === 'unemployed' ? 'Unemployed' : 
                                  data.employmentStatus === 'no_employment_data' ? 'No Employment Data' : 'Unknown',
                  status: data.employmentStatus,
                  compliance: data.compliance,
                  monthlyAverage: data.monthlyAverage,
                  totalRemittance: data.totalRemittance,
                  monthsEmployed: data.monthsEmployed,
                  contractStartDate: data.contractStartDate,
                  contractEndDate: data.contractEndDate,
                  requiredMonthlyAmount: data.requiredMonthlyAmount,
                  lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : data.lastUpdated,
                  lastCalculated: data.lastCalculated?.toDate ? data.lastCalculated.toDate() : data.lastCalculated
                };
              });
              
              // 클라이언트에서 정렬 (고용중인 사용자 우선, 미달성자 우선, 이름순)
              const sortedUsers = usersData.sort((a, b) => {
                // 1. 고용중인 사용자 우선
                if (a.status === 'employed' && b.status !== 'employed') return -1;
                if (b.status === 'employed' && a.status !== 'employed') return 1;
                
                // 2. 고용중인 사용자 중에서는 미달성자 우선
                if (a.status === 'employed' && b.status === 'employed') {
                  if (a.compliance < 100 && b.compliance >= 100) return -1;
                  if (b.compliance < 100 && a.compliance >= 100) return 1;
                }
                
                // 3. 나머지는 이름순
                return (a.name || a.email || '').localeCompare(b.name || b.email || '');
              });
              
              // 전체 사용자 데이터를 메모리에 저장 (페이지 변경 시 재사용)
              setAllUsersCache(sortedUsers);
              
              // 페이지네이션 적용
              const startIndex = (page - 1) * size;
              const endIndex = startIndex + size;
              const paginatedUsers = sortedUsers.slice(startIndex, endIndex);
              
              console.log(`✅ [캐시에서 로드] ${paginatedUsers.length}명의 사용자 데이터 로드 완료 (페이지 ${page}/${Math.ceil(sortedUsers.length / size)})`);
              console.log(`📊 전체 ${sortedUsers.length}명 중 ${startIndex + 1}~${Math.min(endIndex, sortedUsers.length)}번째 표시`);
              console.log('📍 데이터 출처: 배치 작업으로 생성된 캐시 컬렉션 (policyComplianceCache) - 페이지네이션 적용');
              
              // Update stats from cache
              setStatsData({
                totalUsers: statsFromCache.totalUsers || 0,
                nonCompliantUsers: statsFromCache.nonCompliantUsers || 0,
                averageComplianceRate: statsFromCache.averageComplianceRate || 0,
                complianceAchieved: statsFromCache.complianceAchieved || 0,
                severityStats: statsFromCache.severityStats || {
                  critical: 0,
                  warning: 0,
                  moderate: 0,
                  compliant: 0
                }
              });
              
              setUsers(paginatedUsers);
              setLoading(false);
              return true; // 캐시 로드 성공
            } else {
              console.log('⚠️ 캐시 통계 데이터 없음 - 폴백 방식으로 전환');
              return false;
            }
          } catch (innerError) {
            console.warn('⚠️ 캐시 로드 내부 오류:', innerError.message);
            return false;
          }
        })();
        
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('캐시 로드 타임아웃 (10초)')), 10000);
        });
        
        try {
          const cacheLoaded = await Promise.race([cacheLoadPromise, timeoutPromise]);
          if (cacheLoaded) {
            return; // 캐시에서 성공적으로 로드했으면 종료
          }
        } catch (raceError) {
          // 타임아웃 또는 캐시 로드 실패
          console.warn('⚠️ 캐시 로드 실패 또는 타임아웃:', raceError.message);
        }
      } catch (cacheError) {
        console.warn('⚠️ 캐시 로드 외부 오류:', cacheError.message);
      }
      
      // 캐시가 없거나 로드 실패한 경우 - 폴백 (기존 방식)
      console.log('⚠️ 캐시 데이터가 없습니다. 기존 방식으로 로드합니다.');
      
      // Cloud Functions를 사용한 최적화된 조회
      try {
        const result = await getNonCompliantUsers({
          page: 1,
          pageSize: 1000, // 모든 사용자 조회
          minCompliance: 0,
          maxCompliance: 100,
          employmentStatus: 'all',
          sortBy: 'compliance',
          sortOrder: 'desc'
        });
        
        if (result.data.success) {
          const { users: userData } = result.data.data;
          setUsers(userData);
          console.log('✅ [폴백 1: Cloud Functions] 사용자 데이터 로드 완료:', userData.length);
          console.log('📍 데이터 출처: Cloud Function (getNonCompliantUsers) - 캐시 없음');
          return;
        }
      } catch (cloudError) {
        console.warn('⚠️ Cloud Functions 실패, 기존 방식으로 폴백:', cloudError);
      }
      
      // 폴백: 기존 방식 (성능 개선)
      console.log('🔄 기존 방식으로 사용자 데이터 로드');
      const usersRef = collection(db, 'artifacts', appId, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const userList = [];
      
      // 배치 처리로 성능 개선
      const batchSize = 20;
      const totalDocs = usersSnapshot.docs.length;
      
      for (let i = 0; i < totalDocs; i += batchSize) {
        const batch = usersSnapshot.docs.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (userDoc) => {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // Skip admin users
          const isAdmin = userData.role === 'admin' || userData.admin === true;
          if (isAdmin) return null;
          
          // 프로필 서브컬렉션에서 상세 정보 가져오기
          try {
            const profileRef = doc(db, `artifacts/${appId}/users/${userId}/data/profile`);
            const profileSnap = await getDoc(profileRef);
            
            if (profileSnap.exists()) {
              const profileData = profileSnap.data();
              return {
                id: userId,
                ...userData,
                ...profileData,
                name: profileData.name || userData.name || userData.email?.split('@')[0],
                companyName: profileData.employer || userData.companyName || '',
                source: 'artifacts/users'
              };
            } else {
              return {
                id: userId,
                ...userData,
                name: userData.name || userData.email?.split('@')[0],
                companyName: userData.companyName || '',
                source: 'artifacts/users'
              };
            }
          } catch (profileError) {
            console.warn(`프로필 로드 실패 (${userId}):`, profileError);
            return {
              id: userId,
              ...userData,
              name: userData.name || userData.email?.split('@')[0],
              companyName: userData.companyName || '',
              source: 'artifacts/users'
            };
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        const validResults = batchResults.filter(result => result !== null);
        userList.push(...validResults);
        
        console.log(`📊 배치 처리 완료: ${Math.min(i + batchSize, totalDocs)}/${totalDocs}`);
      }
      
      const firebaseUtils = { db, collection, getDocs, query, where };
      
      // 공통 함수를 사용하여 사용자별 Policy Compliance 계산 (설정값 포함)
      const usersWithCompliance = await calculateUsersWithCompliance(
        userList, 
        (userId, appId, firebaseUtils) => calculatePolicyCompliance(userId, appId, firebaseUtils, { requiredMonthlyAmount: settings.requiredMonthlyAmount }), 
        firebaseUtils, 
        appId
      );
      
      // 영어 상태 변환 및 미달성 사용자 우선 정렬
      usersData = usersWithCompliance.map(user => ({
        ...user,
        employmentStatus: user.status === 'employed' ? 'Employed' : 
                         user.status === 'unemployed' ? 'Unemployed' : 
                         user.status === 'no_employment_data' ? 'No Employment Data' : 'Unknown'
      }));
      
      // 미달성 사용자 우선 정렬 (성능 최적화)
      const sortedUsers = usersData.sort((a, b) => {
        // 1. 고용중인 사용자 우선
        if (a.status === 'employed' && b.status !== 'employed') return -1;
        if (b.status === 'employed' && a.status !== 'employed') return 1;
        
        // 2. 고용중인 사용자 중에서는 미달성자 우선
        if (a.status === 'employed' && b.status === 'employed') {
          if (a.compliance < 100 && b.compliance >= 100) return -1;
          if (b.compliance < 100 && a.compliance >= 100) return 1;
        }
        
        // 3. 나머지는 이름순
        return (a.name || a.email || '').localeCompare(b.name || b.email || '');
      });
      
      setUsers(sortedUsers);
      console.log('✅ [폴백 2: 실시간 계산] 사용자 데이터 로드 완료:', sortedUsers.length);
      console.log('📍 데이터 출처: 실시간 계산 (Firestore에서 직접 조회 및 계산) - 캐시 없음');
      console.log('⚠️ 성능 최적화를 위해 "Update Cache" 버튼을 클릭하여 배치 작업을 실행하세요.');
      
    } catch (error) {
      console.error('❌ 사용자 데이터 로드 오류:', error);
      message.error('Failed to load user data.');
    } finally {
      setLoading(false);
    }
  };

  // 설정 로드
  const loadSettings = async () => {
    try {
      const settingsRef = doc(db, 'artifacts', appId, 'settings', 'policy_compliance');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      }
    } catch (error) {
      console.error('설정 로드 오류:', error);
    }
  };

  // 설정 저장
  const saveSettings = async (values) => {
    try {
      const settingsRef = doc(db, 'artifacts', appId, 'settings', 'policy_compliance');
      await setDoc(settingsRef, {
        ...values,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      setSettings(values);
      setShowSettingsModal(false);
      message.success('Settings saved successfully.');
      
      // 사용자 데이터 다시 로드
      loadUsersWithPagination(currentPage, pageSize);
    } catch (error) {
      console.error('설정 저장 오류:', error);
      message.error('Failed to save settings.');
    }
  };

  // 통계 데이터 로드 (캐시 우선, loadUsers와 동일한 방식)
  const loadNonCompliantStats = async () => {
    try {
      console.log('🔄 통계 데이터 로드 시작...');
      
      // 1. 캐시에서 먼저 로드 시도 (loadUsers와 동일한 방식, 서버에서 최신 데이터)
      try {
        const artifactsDocRef = doc(db, `artifacts/${appId}`);
        const adminCollectionRef = collection(artifactsDocRef, 'admin');
        const statsDocRef = doc(adminCollectionRef, 'policyComplianceStats');
        // 서버에서 최신 데이터를 가져오기 위해 getDocFromServer 사용
        let statsSnapshot;
        try {
          statsSnapshot = await getDocFromServer(statsDocRef);
        } catch (serverError) {
          console.warn('⚠️ [통계] 서버에서 로드 실패, 일반 getDoc 사용:', serverError.message);
          statsSnapshot = await getDoc(statsDocRef);
        }
        
        if (statsSnapshot.exists()) {
          const statsFromCache = statsSnapshot.data();
          const cacheAge = statsFromCache.lastUpdated 
            ? new Date() - statsFromCache.lastUpdated.toDate()
            : Infinity;
          const cacheAgeHours = cacheAge / (1000 * 60 * 60);
          
          console.log(`📊 [통계] 캐시에서 로드 (${cacheAgeHours.toFixed(1)}시간 전 업데이트)`);
          
          // 캐시에서 통계 데이터 로드
          setStatsData({
            totalUsers: statsFromCache.totalUsers || 0,
            nonCompliantUsers: statsFromCache.nonCompliantUsers || 0,
            averageComplianceRate: statsFromCache.averageComplianceRate || 0,
            complianceAchieved: statsFromCache.complianceAchieved || 0,
            severityStats: statsFromCache.severityStats || {
              critical: 0,
              warning: 0,
              moderate: 0,
              compliant: 0
            }
          });
          
          console.log('✅ [통계] 캐시에서 로드 완료:', {
            totalUsers: statsFromCache.totalUsers,
            nonCompliantUsers: statsFromCache.nonCompliantUsers,
            averageComplianceRate: statsFromCache.averageComplianceRate,
            complianceAchieved: statsFromCache.complianceAchieved
          });
          
          return; // 캐시에서 성공적으로 로드했으면 종료
        } else {
          console.log('⚠️ [통계] 캐시 통계 데이터 없음, Cloud Function으로 폴백');
        }
      } catch (cacheError) {
        console.warn('⚠️ [통계] 캐시 로드 실패, Cloud Function으로 폴백:', cacheError.message);
      }
      
      // 2. 캐시가 없거나 실패한 경우 Cloud Function 사용
      console.log('🔄 [통계] Cloud Functions에서 통계 데이터 로드 시작...');
      const result = await getNonCompliantStats({});
      console.log('📊 Cloud Function 응답:', JSON.stringify(result, null, 2));
      
      if (result.data.success) {
        const { totalUsers, nonCompliantUsers, averageComplianceRate, complianceAchieved, severityStats } = result.data.data;
        console.log('📈 추출된 데이터:', JSON.stringify({ totalUsers, nonCompliantUsers, averageComplianceRate, complianceAchieved, severityStats }, null, 2));
        
        setStatsData({
          totalUsers,
          nonCompliantUsers,
          averageComplianceRate,
          complianceAchieved,
          severityStats
        });
        console.log('✅ Non-Compliant 통계 로드 완료 (Cloud Function):', {
          totalUsers,
          nonCompliantUsers,
          averageComplianceRate,
          complianceAchieved,
          severityStats
        });
      } else {
        console.warn('⚠️ Cloud Function 응답 실패:', result.data);
      }
    } catch (error) {
      console.error('❌ Non-Compliant 통계 로드 오류:', error);
    }
  };

  useEffect(() => {
    loadSettings();
    loadUsersWithPagination(currentPage, pageSize);
    loadNonCompliantUsersOptimized(); // Non-Compliant 사용자 데이터 로드
    loadNonCompliantStats(); // Non-Compliant 통계 데이터 로드
  }, []);

  // statsData 디버깅
  useEffect(() => {
    console.log('📊 statsData 업데이트:', JSON.stringify({
      totalUsers: statsData.totalUsers,
      nonCompliantUsers: statsData.nonCompliantUsers,
      averageComplianceRate: statsData.averageComplianceRate,
      complianceAchieved: statsData.complianceAchieved,
      severityStats: statsData.severityStats
    }, null, 2));
  }, [statsData]);

  // 필터링된 사용자 목록
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'compliant' && user.compliance >= 100) ||
                         (filterStatus === 'non_compliant' && user.compliance < 100) ||
                         (filterStatus === 'no_data' && user.status === 'no_employment_data');
    
    const matchesEmployment = filterEmployment === 'all' ||
                             (filterEmployment === 'employed' && user.status === 'employed') ||
                             (filterEmployment === 'unemployed' && user.status === 'unemployed') ||
                             (filterEmployment === 'returned_myanmar' && user.status === 'returned_myanmar') ||
                             (filterEmployment === 'no_employment_data' && user.status === 'no_employment_data');
    
    return matchesSearch && matchesFilter && matchesEmployment;
  });

  // 통계 계산 (공통 함수 사용)
  const stats = calculatePolicyComplianceStats(users);

  // 미달성 사용자 목록은 이제 상태로 직접 관리됨

  // 페이지네이션 계산
  const totalPages = Math.ceil(nonCompliantUsers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageUsers = nonCompliantUsers.slice(startIndex, endIndex);

  // 페이지 변경 핸들러 - 페이지 변경 시 메모리 캐시에서 해당 페이지 데이터만 가져오기
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
      // 페이지 크기 변경 시 첫 페이지 데이터 로드
      loadUsersWithPagination(1, size);
    } else {
      // 페이지만 변경된 경우 메모리 캐시에서 slice만 수행 (빠른 처리)
      if (allUsersCache.length > 0) {
        const startIndex = (page - 1) * size;
        const endIndex = startIndex + size;
        const paginatedUsers = allUsersCache.slice(startIndex, endIndex);
        setUsers(paginatedUsers);
        console.log(`📄 페이지 ${page} 변경: 메모리 캐시에서 ${paginatedUsers.length}명 로드 (${startIndex + 1}~${Math.min(endIndex, allUsersCache.length)}번째)`);
      } else {
        // 캐시가 없으면 서버에서 로드
        loadUsersWithPagination(page, size);
      }
    }
  };

  // 테이블 컬럼 정의
  const columns = [
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text || record.email}</div>
          <div className="text-sm text-gray-500">{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text) => text || '-',
    },
    {
      title: 'Employment Status',
      dataIndex: 'employmentStatus',
      key: 'employmentStatus',
      render: (status) => {
        const color = status === 'Employed' ? 'green' : 
                     status === 'Unemployed' ? 'red' : 
                     status === 'No Employment Data' ? 'orange' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Policy Compliance',
      dataIndex: 'compliance',
      key: 'compliance',
      render: (compliance, record) => {
        if (record.status === 'no_employment_data') {
          return <Tag color="orange">No Data</Tag>;
        }
        
        const color = compliance >= 100 ? 'green' : compliance >= 50 ? 'orange' : 'red';
        return (
          <div>
            <Progress 
              percent={compliance} 
              size="small" 
              strokeColor={color === 'green' ? '#52c41a' : color === 'orange' ? '#faad14' : '#ff4d4f'}
            />
            <div className="text-xs text-gray-500 mt-1">
              Monthly Avg: ${record.monthlyAverage || 0}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Contract Period',
      dataIndex: 'contractStartDate',
      key: 'contractPeriod',
      render: (startDate, record) => {
        if (!startDate) return '-';
        return (
          <div className="text-sm">
            <div>{dayjs(startDate).format('YYYY-MM-DD')}</div>
            <div className="text-gray-500">~ {dayjs(record.contractEndDate).format('YYYY-MM-DD')}</div>
          </div>
        );
      },
    },
    {
      title: 'Total Remittance',
      dataIndex: 'totalRemittance',
      key: 'totalRemittance',
      render: (amount) => amount ? `$${amount.toLocaleString()}` : '-',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<Eye className="w-4 h-4" />}
          onClick={(e) => {
            e.stopPropagation();
            handleViewUserDetail(record);
          }}
          className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0"
        >
          View Details
        </Button>
      ),
    },
  ];

  const buildEncourageMessage = (templateKey, user) => {
    const compliance = Number(user?.compliance) || 0;
    const required = Number(settings.requiredMonthlyAmount) || 0;
    const monthlyAvg = Number(user?.monthlyAverage) || 0;
    const name = user?.name || '';
    const template = ENCOURAGE_TEMPLATES.find((t) => t.key === templateKey) || ENCOURAGE_TEMPLATES[0];
    return template.build({ name, compliance, required, monthlyAvg });
  };

  const applyEncourageTemplate = (templateKey, user) => {
    const { title, body } = buildEncourageMessage(templateKey, user);
    setEncourageTemplate(templateKey);
    setEncourageTitle(title);
    setEncourageBody(body);
  };

  // 사용자 상세 정보 모달
  const handleViewUserDetail = (user) => {
    setSelectedUser(user);
    setEncourageAlsoInbox(false);
    setEncourageSentAt(null);
    applyEncourageTemplate(ENCOURAGE_TEMPLATES[0].key, user);
    setShowUserDetailModal(true);
  };

  const handleSendEncourageMessage = async () => {
    if (!selectedUser) return;
    const title = encourageTitle.trim();
    const body = encourageBody.trim();
    if (!title || !body) {
      message.warning('제목과 내용을 입력해 주세요.');
      return;
    }
    const template = ENCOURAGE_TEMPLATES.find((t) => t.key === encourageTemplate);
    setSendingEncourage(true);
    try {
      await sendWarningNotification(selectedUser, {
        title,
        body,
        type: 'remittance_reminder',
        language: template?.language || 'myanmar',
      });
      if (encourageAlsoInbox && selectedUser.email) {
        await messageService.sendAdminMessage(selectedUser.email, selectedUser.name, `${title}\n\n${body}`);
      }
      setEncourageSentAt(new Date());
      message.success(`${selectedUser.name || selectedUser.email}님에게 송금 독려 메시지를 보냈습니다.`);
    } catch (error) {
      const reason = String(error?.message || '');
      message.error(
        reason.includes('No valid FCM tokens')
          ? '이 사용자는 푸시 수신 기기가 등록되어 있지 않습니다. 알림센터에는 저장되었습니다.'
          : '송금 독려 메시지 전송에 실패했습니다.'
      );
    } finally {
      setSendingEncourage(false);
    }
  };

  // 체크박스 선택/해제 함수들
  const handleWorkerSelect = (workerId) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId) 
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleSelectAll = () => {
    const currentPageWorkerIds = currentPageUsers.map(user => user.id);
    const allSelected = currentPageWorkerIds.every(id => selectedWorkers.includes(id));
    
    if (allSelected) {
      // 현재 페이지의 모든 선택 해제
      setSelectedWorkers(prev => prev.filter(id => !currentPageWorkerIds.includes(id)));
    } else {
      // 현재 페이지의 모든 사용자 선택
      setSelectedWorkers(prev => [...new Set([...prev, ...currentPageWorkerIds])]);
    }
  };

  const handleSendWarning = async () => {
    if (selectedWorkers.length === 0) {
      message.warning('Please select workers to send warning messages.');
      return;
    }

    setSendingWarning(true);
    try {
      // 선택된 사용자들의 정보 가져오기
      const selectedUsersData = filteredUsers.filter(user => selectedWorkers.includes(user.id));
      
      // 각 사용자에게 경고 메시지 전송
      for (const user of selectedUsersData) {
        await sendWarningNotification(user);
      }
      
      message.success(`Warning messages sent to ${selectedWorkers.length} workers successfully.`);
      setSelectedWorkers([]);
    } catch (error) {
      console.error('Warning message sending failed:', error);
      message.error('Failed to send warning messages. Please try again.');
    } finally {
      setSendingWarning(false);
    }
  };

  // 경고 알림 전송 함수 (Push Notification Center와 동일한 방식)
  const sendWarningNotification = async (user, custom = {}) => {
    try {
      console.log('🔔 Policy Compliance 경고 알림 전송 시작:', { userId: user.id, email: user.email });
      
      // 미얀마어 경고 메시지 (개인적이고 중요한 메시지)
      const notificationType = custom.type || 'policy_compliance_warning';
      const title = custom.title || "မူဝါဒလိုက်နာမှုသတိပေးချက်"; // Policy Compliance Warning
      const body = custom.body || `သင့်လက်ရှိလိုက်နာမှုနှုန်းမှာ ${user.compliance}% ဖြစ်ပါသည်။ လစဉ်ငွေလွှဲပြန်ပို့မှုလိုအပ်ချက်ကို ဖြည့်ဆည်းရန် လိုအပ်ပါသည်။ မူဝါဒလိုက်နာမှုကို ပိုမိုကောင်းမွန်စွာ ပြုလုပ်ပေးပါ။`; // Your current compliance rate is X%. Please ensure you meet the monthly remittance requirement. Please improve your policy compliance.
      
      // 1. Firestore에 알림 저장 (Push Notification Center와 동일한 방식)
      const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
      
      const notificationData = {
        title: title,
        body: body,
        type: notificationType,
        targetType: 'specific_users',
        targetUsers: [user.id],
        targetCount: 1,
        read: false,
        createdAt: serverTimestamp(),
        createdBy: 'admin',
        data: {
          type: notificationType,
          userId: user.id,
          compliance: user.compliance,
          requiredAmount: settings.requiredMonthlyAmount,
          language: custom.language || 'myanmar',
          priority: 'high',
          actionRequired: true,
          actionUrl: '/remittance'
        }
      };
      
      console.log('📝 Firestore 알림 데이터 저장:', notificationData);
      const docRef = await addDoc(collection(db, `artifacts/${appId}/notifications`), notificationData);
      console.log('✅ Firestore에 알림 저장 완료, 문서 ID:', docRef.id);
      
      // 2. 백엔드 API를 통해 푸시알림 전송 (Push Notification Center와 동일한 방식)
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/notifications/send`;
      console.log('🌐 API URL:', apiUrl);
      
      const payload = {
        userIds: [user.id],
        title: title,
        message: body,
        type: notificationType,
        language: custom.language || 'myanmar',
        sender: 'admin',
        senderName: '👨‍💼 관리자 (Policy Compliance)',
        data: { actionUrl: '/remittance' }
      };
      
      console.log('📦 전송 데이터:', payload);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        mode: 'cors', // 🔥 CORS 모드 명시
        credentials: 'include' // 🔥 인증 정보 포함
      });
      
      console.log('📡 응답 상태:', response.status);
      const responseData = await response.json();
      console.log('📡 응답 내용:', responseData);
      
      if (response.ok && responseData.success) {
        console.log('✅ Policy Compliance 경고 알림 전송 성공:', responseData);
        
        // 푸시알림 발송 시간 기록
        const { updateDoc } = await import('firebase/firestore');
        await updateDoc(docRef, {
          sentAt: serverTimestamp(),
          successCount: responseData.successCount || 1
        });
        
        console.log(`✅ Policy Compliance 경고 알림 전송 완료: ${user.email} (${user.name || 'Unknown'})`);
      } else {
        console.error('❌ Policy Compliance 경고 알림 전송 실패:', response.status, responseData);
        throw new Error(`API error: ${responseData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`❌ Policy Compliance 경고 알림 전송 오류 (${user.email}):`, error);
      throw error;
    }
  };

  // 데이터 내보내기
  const handleExportData = () => {
    const csvData = users.map(user => ({
      '사용자명': user.name || user.email,
      '이메일': user.email,
      '고용업체': user.companyName || '-',
      '고용상태': user.employmentStatus,
      'Policy Compliance': `${user.compliance}%`,
      '월평균 송금액': `$${user.monthlyAverage || 0}`,
      '총 송금액': `$${user.totalRemittance || 0}`,
      '계약시작일': user.contractStartDate || '-',
      '계약종료일': user.contractEndDate || '-'
    }));
    
    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `policy_compliance_${dayjs().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('Data exported successfully.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-auto">
      {/* 상단 헤더 섹션 */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          ></div>
        </div>
        
        <div className="relative w-full px-5 py-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Myanmar Ministry of Labor Header with Official Seal */}
            <div className="flex items-center justify-between mb-3">
              {/* Myanmar Government Official Seal */}
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 shadow-lg">
                  <img 
                    src="/images/MM-Seal-1.png" 
                    alt="Myanmar Government Seal" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div className="text-left">
                  <div className="text-xs font-bold text-white">Republic of the Union of Myanmar</div>
                  <div className="text-[11px] text-blue-200">Ministry of Labour</div>
                </div>
              </div>

              {/* Central Title */}
              <div className="flex-1 text-center">
                <Title level={2} className="text-white mb-1 text-xl font-bold" style={{ color: '#ffffff' }}>
                  Policy Compliance Management System
                </Title>
                <Text className="text-blue-100 text-xs">
                  Comprehensive management system for Myanmar Ministry of Labor policy compliance
                </Text>
              </div>

              {/* Ministry Logo */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-xs font-bold text-white">Ministry of Labour</div>
                  <div className="text-[11px] text-blue-200">Official System</div>
                </div>
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 shadow-lg">
                  <img 
                    src="/images/mol-logo.png" 
                    alt="Ministry of Labour Logo" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30"
              >
                <Globe className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">Real-time Monitoring</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30"
              >
                <Target className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">Automated Compliance</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30"
              >
                <Award className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">Ministry Approved</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="w-full px-5 py-6">
        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center space-x-3">
            <Button 
              type="primary"
              size="middle"
              icon={<Download className="w-4 h-4" />}
              onClick={handleExportData}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 border-0 shadow-md hover:shadow-lg"
            >
              Export Data
            </Button>
            <Button 
              size="middle"
              icon={<Settings className="w-4 h-4" />}
              onClick={() => setShowSettingsModal(true)}
              className="shadow-md hover:shadow-lg"
            >
              Policy Settings
            </Button>
          </div>
          
          <Button 
            size="middle"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => loadUsersWithPagination(currentPage, pageSize)}
            loading={loading}
            disabled={updatingCache}
            className="shadow-md hover:shadow-lg"
          >
            Refresh Data
          </Button>
          <Button 
            size="middle"
            type="primary"
            icon={<RefreshCw className={`w-4 h-4 ${updatingCache ? 'animate-spin' : ''}`} />}
            onClick={async () => {
              try {
                setUpdatingCache(true);
                message.info('배치 작업을 수동으로 실행합니다. 잠시만 기다려주세요... (대규모 사용자일 경우 최대 60분 소요 가능)', 10);
                
                // Call Cloud Function to trigger batch update
                const manualUpdate = httpsCallable(functions, 'manualPolicyComplianceUpdate');
                
                // 타임아웃 설정 (60분 = 3600초, 함수 타임아웃과 동일) - 대규모 사용자(5~6만명) 대비
                const timeoutPromise = new Promise((_, reject) => {
                  setTimeout(() => reject(new Error('함수 실행 타임아웃 (60분 초과)')), 3600000);
                });
                
                const result = await Promise.race([
                  manualUpdate(),
                  timeoutPromise
                ]);
                
                console.log('✅ 배치 작업 완료:', result.data);
                
                // Reload data after batch completes
                setTimeout(() => {
                  loadUsersWithPagination(currentPage, pageSize);
                }, 2000);
                
                message.success('배치 작업 완료', 5);
                message.success(`배치 작업이 성공적으로 완료되었습니다. ${result.data?.processed || 0}명의 사용자 데이터를 업데이트했습니다.`, 10);
              } catch (error) {
                console.error('❌ 배치 작업 트리거 실패:', error);
                let errorMessage = error.message || '알 수 없는 오류';
                
                if (errorMessage.includes('timeout') || errorMessage.includes('타임아웃')) {
                  errorMessage = '함수 실행이 타임아웃되었습니다. Firebase Console에서 함수 로그를 확인하거나 잠시 후 다시 시도해주세요.';
                } else if (errorMessage.includes('permission') || errorMessage.includes('권한')) {
                  errorMessage = '권한이 없습니다. Admin 권한이 있는 계정으로 로그인했는지 확인해주세요.';
                }
                
                message.error('배치 작업 실패', 5);
                message.error(`배치 작업 실행에 실패했습니다: ${errorMessage}\n\nCloud Functions가 배포되어 있는지 확인해주세요.`, 10);
              } finally {
                setUpdatingCache(false);
              }
            }}
            loading={updatingCache}
            disabled={loading || updatingCache}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 border-0 shadow-md hover:shadow-lg"
          >
            Update Cache
          </Button>
        </motion.div>

        {/* Compact Statistics Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mb-4 grid grid-cols-2 lg:grid-cols-4 gap-2"
        >
          {[
            {
              key: 'total',
              label: 'Total Workers',
              value: statsData.totalUsers.toLocaleString(),
              hint: 'Registered',
              Icon: Users,
              accent: 'bg-blue-500',
              iconWrap: 'bg-blue-50 text-blue-600',
            },
            {
              key: 'achieved',
              label: 'Compliance Achieved',
              value: (statsData.totalUsers - statsData.nonCompliantUsers).toLocaleString(),
              hint: '100% Compliant',
              Icon: CheckCircle,
              accent: 'bg-emerald-500',
              iconWrap: 'bg-emerald-50 text-emerald-600',
            },
            {
              key: 'non',
              label: 'Non-Compliant',
              value: statsData.nonCompliantUsers.toLocaleString(),
              hint: 'Needs Attention',
              Icon: AlertTriangle,
              accent: 'bg-orange-500',
              iconWrap: 'bg-orange-50 text-orange-600',
              onClick: () => setActiveTab('non-compliant'),
            },
            {
              key: 'avg',
              label: 'Avg. Compliance',
              value: `${(statsData.averageComplianceRate || 0).toFixed(1)}%`,
              hint: 'Overall',
              Icon: TrendingUp,
              accent: 'bg-indigo-500',
              iconWrap: 'bg-indigo-50 text-indigo-600',
            },
          ].map(({ key, label, value, hint, Icon, accent, iconWrap, onClick }) => (
            <div
              key={key}
              onClick={onClick}
              className={`relative flex items-center gap-2.5 overflow-hidden rounded-lg border border-gray-200 bg-white pl-3.5 pr-3 py-2 shadow-sm ${
                onClick ? 'cursor-pointer hover:border-orange-300 hover:shadow' : ''
              }`}
            >
              <span className={`absolute left-0 top-0 h-full w-1 ${accent}`} aria-hidden />
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${iconWrap}`}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] leading-tight text-gray-500">{label}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-base font-bold leading-tight text-gray-900 tabular-nums">{value}</span>
                  <span className="truncate text-[10px] text-gray-400">{hint}</span>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* 고급 필터 및 검색 섹션 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-6"
        >
          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm floating-card">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Filter className="w-5 h-5 text-blue-600" />
                  </div>
                  <Title level={4} className="mb-0">Advanced Filter & Search</Title>
                </div>
                <Badge count={filteredUsers.length} showZero color="blue">
                  <div className="text-sm text-gray-500">
                    Showing {filteredUsers.length} of {users.length} users
                  </div>
                </Badge>
              </div>
              
              <Row gutter={[16, 12]} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <div className="space-y-2">
                    <Text strong className="text-gray-700">User Search</Text>
                    <Input
                      size="middle"
                      placeholder="Search by name, email, or company"
                      prefix={<Search className="w-4 h-4 text-gray-400" />}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="rounded-lg"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div className="space-y-2">
                    <Text strong className="text-gray-700">Compliance Status</Text>
                    <Select
                      size="middle"
                      placeholder="Filter by status"
                      value={filterStatus}
                      onChange={setFilterStatus}
                      className="w-full rounded-lg"
                    >
                      <Option value="all">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>All Users</span>
                        </div>
                      </Option>
                      <Option value="compliant">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Compliant (100%)</span>
                        </div>
                      </Option>
                      <Option value="non_compliant">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500" />
                          <span>Non-Compliant</span>
                        </div>
                      </Option>
                      <Option value="no_data">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span>No Employment Data</span>
                        </div>
                      </Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div className="space-y-2">
                    <Text strong className="text-gray-700">Employment Status</Text>
                    <Select
                      size="middle"
                      placeholder="Filter by employment"
                      value={filterEmployment}
                      onChange={setFilterEmployment}
                      className="w-full rounded-lg"
                    >
                      <Option value="all">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>All Users</span>
                        </div>
                      </Option>
                      <Option value="employed">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Employed</span>
                        </div>
                      </Option>
                      <Option value="unemployed">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500" />
                          <span>Unemployed</span>
                        </div>
                      </Option>
                      <Option value="returned_myanmar">
                        <div className="flex items-center space-x-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span>Returned to Myanmar</span>
                        </div>
                      </Option>
                      <Option value="no_employment_data">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span>No Employment Data</span>
                        </div>
                      </Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <div className="space-y-2">
                    <Text strong className="text-gray-700">Quick Actions</Text>
                    <div className="flex space-x-2">
                      <Button 
                        size="middle"
                        icon={<Eye className="w-4 h-4" />}
                        onClick={() => setActiveTab('overview')}
                        className={`flex-1 ${activeTab === 'overview' ? 'bg-blue-500 text-white' : ''}`}
                      >
                        Overview
                      </Button>
                      <Button 
                        size="middle"
                        icon={<BarChart3 className="w-4 h-4" />}
                        onClick={() => setActiveTab('analytics')}
                        className={`flex-1 ${activeTab === 'analytics' ? 'bg-blue-500 text-white' : ''}`}
                      >
                        Analytics
                      </Button>
                      <Button 
                        size="middle"
                        icon={<AlertTriangle className="w-4 h-4" />}
                        onClick={() => {
                          setActiveTab('non-compliant');
                          loadNonCompliantUsersOptimized('all');
                        }}
                        className={`flex-1 ${activeTab === 'non-compliant' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-600'}`}
                        loading={nonCompliantLoading}
                      >
                        Non-Compliant
                        {nonCompliantUsers.length > 0 && (
                          <Badge count={nonCompliantUsers.length} className="ml-2" />
                        )}
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </motion.div>

        {/* 탭 내용 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {activeTab === 'overview' && (
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm floating-card">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <Title level={4} className="mb-0">Policy Compliance Overview</Title>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge count={filteredUsers.filter(u => u.compliance >= 100).length} color="green">
                      <span className="text-sm text-green-600 font-medium">Compliant</span>
                    </Badge>
                    <Badge count={filteredUsers.filter(u => u.compliance < 100).length} color="orange">
                      <span className="text-sm text-orange-600 font-medium">Non-Compliant</span>
                    </Badge>
                  </div>
                </div>
                
                <Table
                  columns={columns.map(col => ({
                    ...col,
                    render: col.dataIndex === 'name' ? (text, record) => (
                      <div className="flex items-center space-x-3">
                        <Avatar 
                          size={36} 
                          className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold"
                        >
                          {text?.charAt(0)?.toUpperCase() || record.email?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <div>
                          <div className="font-semibold text-gray-900">{text || record.email}</div>
                          <div className="text-sm text-gray-500">{record.email}</div>
                        </div>
                      </div>
                    ) : col.render
                  }))}
                  dataSource={filteredUsers}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: allUsersCache.length > 0 ? allUsersCache.length : totalUsersCount,
                    size: 'small',
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => 
                      `${range[0]}-${range[1]} of ${total} workers`,
                    onChange: handlePageChange,
                    onShowSizeChange: handlePageChange,
                    className: "modern-pagination"
                  }}
                  scroll={{ x: 1400 }}
                  className="modern-table"
                  size="small"
                  onRow={(record) => ({
                    onClick: () => handleViewUserDetail(record),
                    className: 'cursor-pointer hover:bg-blue-50 transition-colors'
                  })}
                />
              </div>
            </Card>
          )}

          {activeTab === 'analytics' && (
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm floating-card">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                    </div>
                    <Title level={4} className="mb-0">Policy Compliance Analytics</Title>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge count={stats.total} color="blue">
                      <span className="text-sm text-blue-600 font-medium">Total Workers</span>
                    </Badge>
                    <Badge count={stats.averageCompliance} color="purple">
                      <span className="text-sm text-purple-600 font-medium">Avg Rate</span>
                    </Badge>
                  </div>
                </div>
                
                {/* 분석 차트 및 통계 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                  {/* 준수율 분포 */}
                  <Card title="Compliance Distribution" className="border-0 shadow-lg">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">100% Compliant</span>
                        <span className="font-semibold text-green-600">{stats.compliant} workers</span>
                      </div>
                      <Progress 
                        percent={stats.total > 0 ? Math.round((stats.compliant / stats.total) * 100) : 0} 
                        strokeColor="#52c41a"
                        size="small"
                      />
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Non-Compliant</span>
                        <span className="font-semibold text-orange-600">{stats.nonCompliant} workers</span>
                      </div>
                      <Progress 
                        percent={stats.total > 0 ? Math.round((stats.nonCompliant / stats.total) * 100) : 0} 
                        strokeColor="#faad14"
                        size="small"
                      />
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">No Data</span>
                        <span className="font-semibold text-gray-600">{stats.noData} workers</span>
                      </div>
                      <Progress 
                        percent={stats.total > 0 ? Math.round((stats.noData / stats.total) * 100) : 0} 
                        strokeColor="#d9d9d9"
                        size="small"
                      />
                    </div>
                  </Card>

                  {/* 상세 통계 */}
                  <Card title="Detailed Statistics" className="border-0 shadow-lg">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-2.5 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                          <div className="text-sm text-gray-600">Total Workers</div>
                        </div>
                        <div className="text-center p-2.5 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{stats.compliant}</div>
                          <div className="text-sm text-gray-600">Compliant</div>
                        </div>
                        <div className="text-center p-2.5 bg-orange-50 rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">{stats.nonCompliant}</div>
                          <div className="text-sm text-gray-600">Non-Compliant</div>
                        </div>
                        <div className="text-center p-2.5 bg-purple-50 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">{stats.averageCompliance}%</div>
                          <div className="text-sm text-gray-600">Average Rate</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Non-Compliant 사용자 요약 및 링크 */}
                <Card 
                  title={
                    <div className="flex items-center justify-between">
                      <span>
                        Non-Compliant Workers Summary
                        <span className="text-sm text-gray-500 ml-2">
                          ({nonCompliantUsers.length} workers need attention)
                        </span>
                      </span>
                      <Button 
                        type="primary"
                        size="small"
                        icon={<AlertTriangle className="w-4 h-4" />}
                        onClick={() => {
                          setActiveTab('non-compliant');
                          loadNonCompliantUsersOptimized('all');
                        }}
                        className="bg-red-500 hover:bg-red-600 border-0"
                      >
                        View Details
                      </Button>
                    </div>
                  } 
                  className="border-0 shadow-lg"
                >
                  <div className="space-y-4">
                    {/* 심각도별 요약 */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-2.5 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                          {nonCompliantUsers.filter(u => u.compliance < 25).length}
                        </div>
                        <div className="text-sm text-gray-600">Critical (0-24%)</div>
                      </div>
                      <div className="text-center p-2.5 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">
                          {nonCompliantUsers.filter(u => u.compliance >= 25 && u.compliance < 50).length}
                        </div>
                        <div className="text-sm text-gray-600">Warning (25-49%)</div>
                      </div>
                      <div className="text-center p-2.5 bg-yellow-50 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">
                          {nonCompliantUsers.filter(u => u.compliance >= 50 && u.compliance < 100).length}
                        </div>
                        <div className="text-sm text-gray-600">Moderate (50-99%)</div>
                      </div>
                    </div>
                    
                    {/* 상위 3명 미리보기 */}
                    {nonCompliantUsers.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700 mb-2">Top Priority Cases:</div>
                        {nonCompliantUsers.slice(0, 3).map((user, index) => (
                          <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-xs font-bold text-red-600">
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {user.name || user.email}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {user.companyName || 'Unknown Company'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-red-600">{user.compliance}%</div>
                              <div className="text-xs text-gray-500">Compliance</div>
                            </div>
                          </div>
                        ))}
                        {nonCompliantUsers.length > 3 && (
                          <div className="text-center text-sm text-gray-500 py-2">
                            +{nonCompliantUsers.length - 3} more workers need attention
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                        <div>All employed workers are compliant! 🎉</div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </Card>
          )}

          {activeTab === 'non-compliant' && (
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm floating-card">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <Title level={4} className="mb-0">Non-Compliant Users Management</Title>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge count={nonCompliantUsers.length} color="red">
                      <span className="text-sm text-red-600 font-medium">Need Attention</span>
                    </Badge>
                    {selectedNonCompliantUsers.length > 0 && (
                      <Badge count={selectedNonCompliantUsers.length} color="orange">
                        <span className="text-sm text-orange-600 font-medium">Selected</span>
                      </Badge>
                    )}
                    {performanceStats.lastUpdate && (
                      <Badge color="blue">
                        <span className="text-xs text-blue-500">
                          ⚡ {performanceStats.queryTime.toFixed(0)}ms
                          {performanceStats.cacheHit && ' (Cached)'}
                        </span>
                      </Badge>
                    )}
                  </div>
                </div>

                {/* 필터 및 액션 버튼 */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <Select
                      placeholder="Filter by severity"
                      style={{ width: 200 }}
                      onChange={(value) => {
                        if (value === 'all') {
                          loadNonCompliantUsersOptimized('all');
                        } else {
                          loadNonCompliantUsersOptimized(value);
                        }
                      }}
                    >
                      <Option value="all">All Severities</Option>
                      <Option value="critical">Critical (0-24%)</Option>
                      <Option value="warning">Warning (25-49%)</Option>
                      <Option value="moderate">Moderate (50-99%)</Option>
                    </Select>
                    
                    <Button
                      icon={<RefreshCw className="w-4 h-4" />}
                      onClick={() => loadNonCompliantUsersOptimized('all')}
                      loading={nonCompliantLoading}
                      size="middle"
                    >
                      Refresh
                    </Button>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={handleSelectAllNonCompliantUsers}
                      className="text-sm"
                      size="middle"
                    >
                      {selectedNonCompliantUsers.length === nonCompliantUsers.length 
                        ? 'Deselect All' 
                        : 'Select All'
                      }
                    </Button>
                    
                    <Button
                      type="primary"
                      icon={<Send className="w-4 h-4" />}
                      onClick={handleSendRemittanceNotification}
                      loading={sendingRemittanceNotification}
                      disabled={selectedNonCompliantUsers.length === 0}
                      className="bg-orange-500 hover:bg-orange-600 border-0"
                      size="middle"
                    >
                      {sendingRemittanceNotification 
                        ? 'Sending...' 
                        : `Send Remittance Reminder (${selectedNonCompliantUsers.length})`
                      }
                    </Button>
                    
                    <Button
                      type={realTimeMonitoring ? 'primary' : 'default'}
                      icon={<Activity className="w-4 h-4" />}
                      onClick={toggleRealTimeMonitoring}
                      size="middle"
                    >
                      {realTimeMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
                    </Button>
                  </div>
                </div>

                {/* Non-Compliant 사용자 목록 */}
                <Table
                  columns={[
                    {
                      title: (
                        <Checkbox
                          checked={selectedNonCompliantUsers.length === nonCompliantUsers.length && nonCompliantUsers.length > 0}
                          indeterminate={selectedNonCompliantUsers.length > 0 && selectedNonCompliantUsers.length < nonCompliantUsers.length}
                          onChange={handleSelectAllNonCompliantUsers}
                        />
                      ),
                      key: 'select',
                      width: 50,
                      render: (_, record) => (
                        <Checkbox
                          checked={selectedNonCompliantUsers.includes(record.id)}
                          onChange={(e) => handleSelectNonCompliantUser(record.id, e.target.checked)}
                        />
                      )
                    },
                    {
                      title: 'User',
                      dataIndex: 'name',
                      key: 'name',
                      render: (text, record) => (
                        <div className="flex items-center space-x-3">
                        <Avatar 
                          size={36} 
                            className="bg-gradient-to-br from-red-500 to-orange-600 text-white font-semibold"
                          >
                            {text?.charAt(0)?.toUpperCase() || record.email?.charAt(0)?.toUpperCase() || 'U'}
                          </Avatar>
                          <div>
                            <div className="font-semibold text-gray-900">{text || record.email}</div>
                            <div className="text-sm text-gray-500">{record.email}</div>
                          </div>
                        </div>
                      )
                    },
                    {
                      title: 'Company',
                      dataIndex: 'companyName',
                      key: 'companyName',
                      render: (text) => text || 'Unknown Company'
                    },
                    {
                      title: 'Compliance',
                      dataIndex: 'compliance',
                      key: 'compliance',
                      render: (compliance) => (
                        <div className="text-center">
                          <div className={`text-lg font-bold ${
                            compliance < 25 ? 'text-red-600' : 
                            compliance < 50 ? 'text-orange-600' : 
                            'text-yellow-600'
                          }`}>
                            {compliance}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {compliance < 25 ? 'Critical' : 
                             compliance < 50 ? 'Warning' : 'Moderate'}
                          </div>
                        </div>
                      )
                    },
                    {
                      title: 'Status',
                      dataIndex: 'employmentStatus',
                      key: 'employmentStatus',
                      render: (status) => (
                        <Badge 
                          color={status === 'employed' ? 'green' : 'orange'}
                          text={status || 'Unknown'}
                        />
                      )
                    },
                    {
                      title: 'Last Updated',
                      dataIndex: 'lastCalculated',
                      key: 'lastCalculated',
                      render: (date) => date ? new Date(date).toLocaleDateString() : 'Unknown'
                    }
                  ]}
                  dataSource={nonCompliantUsers}
                  rowKey="id"
                  loading={nonCompliantLoading}
                  pagination={{
                    pageSize: 20,
                    size: 'small',
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => 
                      `${range[0]}-${range[1]} of ${total} non-compliant workers`,
                    className: "modern-pagination"
                  }}
                  scroll={{ x: 1000 }}
                  className="modern-table"
                  size="small"
                  onRow={(record) => ({
                    onClick: () => handleViewUserDetail(record),
                    className: 'cursor-pointer hover:bg-red-50 transition-colors'
                  })}
                />
              </div>
            </Card>
          )}
        </motion.div>

        {/* 고급 설정 모달 */}
        <Modal
          title={
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <span>Policy Compliance Settings</span>
            </div>
          }
          open={showSettingsModal}
          onCancel={() => setShowSettingsModal(false)}
          footer={null}
          width={560}
          className="modern-modal"
        >
          <div className="p-4">
            <Alert
              message="Policy Settings Information"
              description="Set the monthly average remittance requirement to manage Policy Compliance standards. This setting affects the compliance calculation for all users."
              type="info"
              showIcon
              className="mb-5"
            />
            
            <Form
              form={form}
              layout="vertical"
              initialValues={settings}
              onFinish={saveSettings}
            >
              <Form.Item
                label={
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-semibold">Monthly Average Remittance Requirement (USD)</span>
                  </div>
                }
                name="requiredMonthlyAmount"
                rules={[{ required: true, message: 'Please enter the monthly average remittance requirement.' }]}
              >
                <InputNumber
                  min={0}
                  max={10000}
                  step={50}
                  className="w-full"
                  addonAfter="USD"
                  size="large"
                />
              </Form.Item>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-5">
                <div className="flex items-center space-x-2 mb-2">
                  <Info className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-700">Current Settings Information</span>
                </div>
                <div className="text-sm text-gray-600">
                  • Current Requirement: <span className="font-semibold">${settings.requiredMonthlyAmount} USD</span><br/>
                  • 100% Compliance achieved when remitting this amount or more<br/>
                  • Set according to Myanmar Ministry of Labor policy standards
                </div>
              </div>
              
              <Form.Item>
                <div className="flex justify-end space-x-3">
                  <Button 
                    size="middle"
                    onClick={() => setShowSettingsModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    size="middle"
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 border-0"
                  >
                    Save Settings
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </div>
        </Modal>

        {/* 사용자 상세 정보 모달 */}
        <Modal
          title={
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <span>User Details</span>
            </div>
          }
          open={showUserDetailModal}
          onCancel={() => setShowUserDetailModal(false)}
          footer={null}
          width={760}
          className="modern-modal"
        >
          {selectedUser && (
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 기본 정보 */}
                <Card title="Basic Information" className="border-0 shadow-lg">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="User Name">
                      <div className="flex items-center space-x-2">
                        <Avatar 
                          size={32} 
                          className="bg-gradient-to-br from-blue-500 to-purple-600 text-white"
                        >
                          {selectedUser.name?.charAt(0)?.toUpperCase() || selectedUser.email?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <span className="font-medium">{selectedUser.name || selectedUser.email}</span>
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
                    <Descriptions.Item label="Company">{selectedUser.companyName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Employment Status">
                      <Tag color={selectedUser.employmentStatus === 'Employed' ? 'green' : 'orange'}>
                        {selectedUser.employmentStatus}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Policy Compliance 정보 */}
                <Card title="Policy Compliance Status" className="border-0 shadow-lg">
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold mb-2">
                        <span className={selectedUser.compliance >= 100 ? 'text-green-600' : 'text-orange-600'}>
                          {selectedUser.compliance}%
                        </span>
                      </div>
                      <Progress 
                        percent={selectedUser.compliance} 
                        strokeColor={selectedUser.compliance >= 100 ? '#52c41a' : '#faad14'}
                        size="large"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="font-semibold text-gray-700">Monthly Average</div>
                        <div className="text-lg font-bold text-blue-600">
                          ${selectedUser.monthlyAverage || 0}
                        </div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="font-semibold text-gray-700">Total Remittance</div>
                        <div className="text-lg font-bold text-green-600">
                          ${selectedUser.totalRemittance || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* 계약 정보 */}
              {selectedUser.contractStartDate && (
                <Card title="고용 계약 정보" className="border-0 shadow-lg mt-5">
                  <Timeline>
                    <Timeline.Item 
                      dot={<Calendar className="w-4 h-4 text-blue-600" />}
                      color="blue"
                    >
                      <div className="font-medium">계약 시작일</div>
                      <div className="text-gray-600">{dayjs(selectedUser.contractStartDate).format('YYYY년 MM월 DD일')}</div>
                    </Timeline.Item>
                    <Timeline.Item 
                      dot={<Calendar className="w-4 h-4 text-green-600" />}
                      color="green"
                    >
                      <div className="font-medium">계약 종료일</div>
                      <div className="text-gray-600">{dayjs(selectedUser.contractEndDate).format('YYYY년 MM월 DD일')}</div>
                    </Timeline.Item>
                  </Timeline>
                </Card>
              )}

              {Number(selectedUser.compliance) < 100 && (
                <div className="mt-5 rounded-xl border border-orange-200 bg-orange-50/50 p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                        <Send className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">송금 독려 메시지 보내기</div>
                        <div className="text-xs text-gray-500">
                          푸시 알림과 앱 알림센터로 전송되며, 알림을 누르면 송금 화면으로 이동합니다.
                        </div>
                      </div>
                    </div>
                    <Select
                      size="small"
                      value={encourageTemplate}
                      onChange={(key) => applyEncourageTemplate(key, selectedUser)}
                      style={{ minWidth: 180 }}
                      options={ENCOURAGE_TEMPLATES.map((t) => ({ value: t.key, label: t.label }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Input
                      value={encourageTitle}
                      onChange={(e) => setEncourageTitle(e.target.value)}
                      placeholder="제목"
                      maxLength={80}
                    />
                    <Input.TextArea
                      value={encourageBody}
                      onChange={(e) => setEncourageBody(e.target.value)}
                      placeholder="메시지 내용"
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      maxLength={500}
                      showCount
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <Checkbox
                      checked={encourageAlsoInbox}
                      disabled={!selectedUser.email}
                      onChange={(e) => setEncourageAlsoInbox(e.target.checked)}
                    >
                      <span className="text-xs text-gray-600">앱 메시지함에도 보내기</span>
                    </Checkbox>
                    <div className="flex items-center gap-2">
                      {encourageSentAt && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle className="h-3.5 w-3.5" />
                          {dayjs(encourageSentAt).format('HH:mm')} 전송 완료
                        </span>
                      )}
                      <Button
                        type="primary"
                        icon={<Send className="h-3.5 w-3.5" />}
                        loading={sendingEncourage}
                        onClick={handleSendEncourageMessage}
                        className="bg-orange-500 border-orange-500 hover:!bg-orange-600"
                      >
                        메시지 보내기
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-5">
                <Button 
                  size="middle"
                  onClick={() => setShowUserDetailModal(false)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 border-0 text-white"
                >
                  닫기
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default PolicyCompliancePage;
